/**
 * CSS Variables Platform Adapter
 *
 * This is the reference implementation for platform adapters, generating
 * CSS custom properties (CSS variables) from design tokens.
 */

import {
  PlatformAdapter,
  TokenSet,
  DesignToken,
  TokenGroup,
  ExportOptions,
  ValidationResult,
  ValidationError,
  ValidationWarning,
  TokenType,
  ColorValue,
  DimensionValue,
  FontValue,
  DurationValue,
  CubicBezierValue,
  ShadowValue,
  GradientValue,
  BorderValue,
  TransitionValue,
  TokenExportResult,
} from "../../types/designTokens";
import { BasePlatformAdapter } from "./basePlatformAdapter";

/**
 * CSS Variables Adapter - Reference Implementation
 */
export class CSSVariablesAdapter
  extends BasePlatformAdapter
  implements PlatformAdapter
{
  constructor() {
    super({
      name: "CSS Variables",
      version: "1.0.0",
      supportedTypes: [
        "color",
        "dimension",
        "font",
        "duration",
        "cubicBezier",
        "number",
        "string",
        "boolean",
        "shadow",
        "gradient",
        "border",
        "transition",
        "strokeStyle",
        "fontFamily",
        "fontWeight",
        "fontSize",
        "lineHeight",
        "letterSpacing",
      ],
    });
  }

  /**
   * Generate CSS variables from token set
   */
  async generate(
    tokens: TokenSet,
    options: ExportOptions = {},
  ): Promise<string> {
    const {
      prefix = "",
      includeMetadata = true,
      includeComments = true,
      minify = false,
      namingConvention = "kebab-case",
    } = options;

    let css = "";

    // Add header comment if metadata is included
    if (includeMetadata && tokens.$metadata) {
      css += this.generateHeaderComment(tokens.$metadata);
    }

    // Generate CSS variables
    css += ":root {\n";

    if (tokens.tokens) {
      // Process each token in tokens object
      Object.entries(tokens.tokens).forEach(([key, value]) => {
        css += this.processTokenGroup(
          value as TokenGroup | DesignToken,
          key,
          prefix,
          namingConvention,
          includeComments,
          1,
        );
      });
    }

    css += "}\n\n";

    // Add utility classes if requested
    if (options.includeUtilities) {
      css += this.generateUtilityClasses(tokens, prefix, namingConvention);
    }

    // Minify if requested
    if (minify) {
      css = this.minifyCSS(css);
    }

    return css;
  }

  /**
   * Process a token group and generate CSS variables
   */
  private processTokenGroup(
    group: TokenGroup | DesignToken,
    basePath: string,
    prefix: string,
    namingConvention: string,
    includeComments: boolean,
    indent: number,
  ): string {
    let css = "";

    if (this.isTokenGroup(group)) {
      const tokenGroup = group as TokenGroup;

      // Add group comment if comments are enabled
      if (includeComments && tokenGroup.$description) {
        css += `${"  ".repeat(indent)}/* ${tokenGroup.$description} */\n`;
      }

      // Process children
      if (tokenGroup.children) {
        Object.entries(tokenGroup.children).forEach(([key, value]) => {
          const childPath = basePath ? `${basePath}.${key}` : key;
          css += this.processTokenGroup(
            value as TokenGroup | DesignToken,
            childPath,
            prefix,
            namingConvention,
            includeComments,
            indent + 1,
          );
        });
      }
    } else {
      const token = group as DesignToken;
      const variableName = this.generateVariableName(
        basePath,
        prefix,
        namingConvention,
      );
      const cssValue = this.tokenToCSSValue(token, namingConvention);

      // Add token comment if comments are enabled
      if (includeComments && token.$description) {
        css += `${"  ".repeat(indent)}/* ${token.$description} */\n`;
      }

      css += `${"  ".repeat(indent)}${variableName}: ${cssValue};\n`;
    }

    return css;
  }

  /**
   * Convert design token to CSS value
   */
  private tokenToCSSValue(
    token: DesignToken,
    namingConvention: string,
  ): string {
    const value = token.$value;

    switch (token.$type) {
      case "color":
        return this.colorToCSS(value as ColorValue);

      case "dimension":
      case "fontSize":
      case "lineHeight":
      case "letterSpacing":
        return this.dimensionToCSS(value as DimensionValue);

      case "font":
        return this.fontToCSS(value as FontValue);

      case "duration":
        return this.durationToCSS(value as DurationValue);

      case "cubicBezier":
        return this.cubicBezierToCSS(value as CubicBezierValue);

      case "shadow":
        return this.shadowToCSS(value as ShadowValue);

      case "gradient":
        return this.gradientToCSS(value as GradientValue);

      case "border":
        return this.borderToCSS(value as BorderValue);

      case "transition":
        return this.transitionToCSS(value as TransitionValue);

      case "number":
        return String(value);

      case "string":
      case "fontFamily":
      case "fontWeight":
      case "strokeStyle":
        return `"${value}"`;

      case "boolean":
        return value ? "true" : "false";

      case "link": {
        // Handle token references
        if (typeof value === "string" && value.startsWith("{")) {
          const refPath = value.slice(1, -1);
          return `var(--${this.formatVariableName(refPath, namingConvention)})`;
        }
        return String(value);
      }

      default:
        return String(value);
    }
  }

  /**
   * Convert color value to CSS
   */
  private colorToCSS(color: ColorValue): string {
    const [r, g, b] = color.channels.map((c) => Math.round(c * 255));
    const hex = `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;

    if (color.alpha !== undefined && color.alpha < 1) {
      return `${hex}${Math.round(color.alpha * 255)
        .toString(16)
        .padStart(2, "0")}`;
    }

    return hex;
  }

  /**
   * Convert dimension value to CSS
   */
  private dimensionToCSS(dimension: DimensionValue): string {
    return `${dimension.value}${dimension.unit}`;
  }

  /**
   * Convert font value to CSS
   */
  private fontToCSS(font: FontValue): string {
    const parts: string[] = [];

    parts.push(font.fontFamily.map((f) => `"${f}"`).join(", "));

    if (font.fontSize) {
      parts.push(this.dimensionToCSS(font.fontSize));
    }

    if (font.fontWeight) {
      parts.push(String(font.fontWeight));
    }

    if (font.lineHeight) {
      if (typeof font.lineHeight === "string") {
        parts.push(font.lineHeight);
      } else {
        parts.push(this.dimensionToCSS(font.lineHeight));
      }
    }

    if (font.letterSpacing) {
      parts.push(this.dimensionToCSS(font.letterSpacing));
    }

    if (font.fontStyle) {
      parts.push(font.fontStyle);
    }

    if (font.fontStretch) {
      parts.push(font.fontStretch);
    }

    return parts.join(" ");
  }

  /**
   * Convert duration value to CSS
   */
  private durationToCSS(duration: DurationValue): string {
    return `${duration.value}${duration.unit}`;
  }

  /**
   * Convert cubic bezier value to CSS
   */
  private cubicBezierToCSS(bezier: CubicBezierValue): string {
    return `cubic-bezier(${bezier.x1}, ${bezier.y1}, ${bezier.x2}, ${bezier.y2})`;
  }

  /**
   * Convert shadow value to CSS
   */
  private shadowToCSS(shadow: ShadowValue): string {
    const parts: string[] = [];

    parts.push(this.dimensionToCSS(shadow.offsetX));
    parts.push(this.dimensionToCSS(shadow.offsetY));

    if (shadow.blur) {
      parts.push(this.dimensionToCSS(shadow.blur));
    }

    if (shadow.spread) {
      parts.push(this.dimensionToCSS(shadow.spread));
    }

    parts.push(this.colorToCSS(shadow.color));

    if (shadow.inset) {
      parts.push("inset");
    }

    return parts.join(" ");
  }

  /**
   * Convert gradient value to CSS
   */
  private gradientToCSS(gradient: GradientValue): string {
    const colorStops = gradient.colorStops
      .map((stop) => {
        const color = this.colorToCSS(stop.color);
        const position = stop.position
          ? this.dimensionToCSS(stop.position)
          : "";
        return position ? `${color} ${position}` : color;
      })
      .join(", ");

    switch (gradient.type) {
      case "linear":
        const direction = gradient.direction || "to bottom";
        return `linear-gradient(${direction}, ${colorStops})`;

      case "radial":
        return `radial-gradient(circle, ${colorStops})`;

      case "conic":
        const fromAngle = gradient.direction || "0deg";
        return `conic-gradient(from ${fromAngle}, ${colorStops})`;

      default:
        return `linear-gradient(to bottom, ${colorStops})`;
    }
  }

  /**
   * Convert border value to CSS
   */
  private borderToCSS(border: BorderValue): string {
    const width = this.dimensionToCSS(border.width);
    const color = this.colorToCSS(border.color);
    return `${width} ${border.style} ${color}`;
  }

  /**
   * Convert transition value to CSS
   */
  private transitionToCSS(transition: TransitionValue): string {
    const duration = this.durationToCSS(transition.duration);
    const delay = transition.delay
      ? this.durationToCSS(transition.delay)
      : "0ms";

    let timingFunction: string;
    if (typeof transition.timingFunction === "string") {
      timingFunction = transition.timingFunction;
    } else {
      timingFunction = this.cubicBezierToCSS(
        transition.timingFunction as CubicBezierValue,
      );
    }

    return `${duration} ${timingFunction} ${delay}`;
  }

  /**
   * Generate CSS variable name
   */
  private generateVariableName(
    path: string,
    prefix: string,
    namingConvention: string,
  ): string {
    const formattedPath = this.formatVariableName(path, namingConvention);
    return `--${prefix}${formattedPath}`;
  }

  /**
   * Format variable name according to naming convention
   */
  private formatVariableName(path: string, namingConvention: string): string {
    switch (namingConvention) {
      case "camelCase":
        return path
          .split(".")
          .map((part, index) =>
            index === 0 ? part : part.charAt(0).toUpperCase() + part.slice(1),
          )
          .join("");

      case "kebab-case":
        return path.replace(/\./g, "-");

      case "snake_case":
        return path.replace(/\./g, "_");

      case "PascalCase":
        return path
          .split(".")
          .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
          .join("");

      default:
        return path.replace(/\./g, "-");
    }
  }

  /**
   * Generate header comment
   */
  private generateHeaderComment(metadata: Record<string, unknown>): string {
    const lines = [
      `Generated by ${this.name} v${this.version}`,
      `Token Set: ${metadata.name || "Untitled"}`,
      `Description: ${metadata.description || "No description"}`,
      `Generated: ${new Date().toLocaleString()}`,
    ];

    return `/*\n${lines.map((line) => ` * ${line}`).join("\n")} */\n\n`;
  }

  /**
   * Generate utility classes
   */
  private generateUtilityClasses(
    tokens: TokenSet,
    prefix: string,
    namingConvention: string,
  ): string {
    let css = "/* Utility Classes */\n";

    // Generate color utilities
    if (tokens.tokens?.color) {
      const colorGroup = tokens.tokens.color as TokenGroup;
      if (colorGroup.children) {
        Object.entries(colorGroup.children).forEach(([name, token]) => {
          if (token && typeof token === "object" && "$type" in token) {
            const designToken = token as DesignToken;
            if (designToken.$type === "color") {
              const variableName = this.formatVariableName(
                `color.${name}`,
                namingConvention,
              );
              css += `.bg-${name} { background-color: var(--${variableName}); }\n`;
              css += `.text-${name} { color: var(--${variableName}); }\n`;
              css += `.border-${name} { border-color: var(--${variableName}); }\n`;
            }
          }
        });
      }
    }

    return css;
  }

  /**
   * Minify CSS by removing comments and whitespace
   */
  private minifyCSS(css: string): string {
    return css
      .replace(/\/\*[\s\S]*?\*\//g, "") // Remove comments
      .replace(/\s+/g, " ") // Collapse whitespace
      .replace(/;\s*}/g, "}") // Remove semicolons before closing braces
      .trim();
  }

  /**
   * Check if value is a token group
   */
  private isTokenGroup(value: any): boolean {
    return (
      value &&
      typeof value === "object" &&
      (!value.$type || value.$type === "group")
    );
  }
}
